import { createFileRoute } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { BulkExport, type Candidate } from "@/components/BulkExport";
import { resolveAssets } from "@/lib/symbol-asset";
import { useTray } from "@/lib/clipboard-tray";
import { useLibrary } from "@/lib/library";

export const Route = createFileRoute("/export")({
  head: () => ({
    meta: [
      { title: "Bulk export symbols as SVG & PNG — download a ZIP" },
      {
        name: "description",
        content:
          "Export many Unicode symbols at once. Pick from your clipboard tray and favourites, choose SVG or PNG and a size, and download everything as a single ZIP — generated in your browser, no upload.",
      },
      { property: "og:title", content: "Bulk export symbols as SVG & PNG" },
      {
        property: "og:description",
        content:
          "Select symbols and download them all as SVG/PNG in one ZIP, built in your browser.",
      },
      { property: "og:url", content: abs("/export") },
    ],
    links: [{ rel: "canonical", href: abs("/export") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Bulk symbol export",
          applicationCategory: "UtilityApplication",
          operatingSystem: "Any",
          url: "/export",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: ExportPage,
});

function ExportPage() {
  const tray = useTray();
  const { favorites, recentlyViewed } = useLibrary();

  // Build candidates source-by-source; BulkExport de-dupes by slug.
  const candidates: Candidate[] = [
    ...resolveAssets(tray.items).map((a) => ({ ...a, source: "Tray" })),
    ...resolveAssets(favorites).map((a) => ({ ...a, source: "Favorites" })),
    ...resolveAssets(recentlyViewed.map((e) => e.symbol)).map((a) => ({ ...a, source: "Recent" })),
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          Bulk <span className="text-gradient">export</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-foreground/85">
          Grab a whole set of symbols at once. Everything from your clipboard tray, favourites and
          recently viewed is gathered below — pick what you want, choose SVG or PNG, and download a
          single ZIP. It's all built right here in your browser; nothing is uploaded.
        </p>

        <BulkExport candidates={candidates} />

        <section aria-labelledby="how-heading" className="mt-14 max-w-2xl">
          <h2 id="how-heading" className="font-display text-xl font-semibold">
            About the files
          </h2>
          <p className="mt-3 text-foreground/85">
            SVGs are crisp vector files that scale to any size; PNGs are transparent raster images
            at the size you choose. Each file is named after the symbol's slug — for example{" "}
            <code className="rounded bg-surface/60 px-1 font-mono text-sm">black-star.svg</code> —
            so a large export stays easy to navigate. The ZIP is assembled on your device with no
            compression overhead, so the download starts instantly.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
